import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DateAdapter, MatSidenav } from '@angular/material';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { from, fromEvent, Subject } from 'rxjs';
import { bufferCount, filter, first, map, mergeMap, sequenceEqual, takeUntil } from 'rxjs/operators';
import {
  ApiEvents,
  AppConfig,
  DataManagementService,
  DBService,
  DeviceService,
  DialogService,
  HttpService,
  Logger,
  LoggingService,
  LoginService,
  ScreenSize,
  TranslateService,
} from 'vas-mobile-api';

import { environment } from '../environments/environment';
import { MenuConfig } from './models/config';
import { DataExchangeService } from './services/dataexchange.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})

export class AppComponent implements OnInit, OnDestroy {
  public logger: Logger;
  public userName = null;
  public userClass = null;
  public userType = null;
  @ViewChild('sidenav', { static: false }) private myNav: MatSidenav;
  public titleBar = '';
  public isReady = false;
  public showBurgerMenu = true;
  public showFilter: boolean;
  public showRefresh: boolean;
  private loginRoute = '/login';
  private deliveriesListRoute = '/deliveriesList';
  private statusListRoute = '/statusList';
  private setPasswordRoute = '/pro';
  private dashboardRoute = '/dashboard';
  private deliveryDetailRoute = '/deliveryDetail';
  private transactionDetailRoute = '/transactionDetail';
  private reportDetailRoute = '/reportDetail';
  public sidenavMode = 'over';
  public viewMode = 'ltr';
  public showLoading = false;
  public showBackBtn = false;

  public isLoginComponent = true;
  private ngUnsubscribe: Subject<void> = new Subject();
  public showMobile: boolean;
  public menuConfig: MenuConfig;
  public isVIP: boolean;

  private activeMenuItem = 0;

  constructor(

    private dialogService: DialogService,
    private loggingService: LoggingService,
    private translateVAS: TranslateService,
    private loginService: LoginService,
    private dataexchangeService: DataExchangeService,
    private router: Router,
    private dataManagementVasService: DataManagementService,
    private apiEvents: ApiEvents,
    private deviceService: DeviceService,
    private cdr: ChangeDetectorRef,
    private httpService: HttpService,
    private dateAdapter: DateAdapter<Date>,
    // private vipResolver: VIPResolverService,
    private appConfig: AppConfig,
    private dbService: DBService

  ) {

    this.apiEvents.ready().then(() => {
      const currLang = this.translateVAS.getLanguage();
      if (currLang === 'AR') {
        this.dataexchangeService.changeViewMode('rtl');
      }

      let localeExtended = 'en-US';
      switch (currLang) {
        case 'DE':
          localeExtended = 'de-DE';
          break;
        case 'AR':
          localeExtended = 'ar-SY';
          break;
      }

      this.dateAdapter.setLocale(localeExtended);

      this.deviceService.size.pipe(takeUntil(this.ngUnsubscribe)).subscribe((size) => {
        this.sidenavMode = size <= ScreenSize.sm ? 'over' : 'side';
        this.showMobile = size <= ScreenSize.sm;
        if (this.router.url.startsWith(this.dashboardRoute)) {
          this.showRefresh = !this.showMobile;
        }
      });

      this.isReady = true;

    });
  }

  isActive(i: number): boolean {
    return i === this.activeMenuItem;
  }

  menuAvailable(access: string[]): boolean {
    // First check if this is a supplier login, than only show login menu entries
    if (this.userType === 'SUPP') {
      if (access.indexOf(this.userType) > -1) {
        return true;
      } else {
        return false;
      }
    } else {
      if (access.indexOf(this.userClass) > -1) {
        return true;
      } else {
        return false;
      }
    }
  }

  ngOnInit() {
    this.logger = this.loggingService.getLogger(AppComponent.name);
    this.dbService.deleteApiItem('latestUsers');
    this.dbService.deleteItem('latestUsers');
    this.router.events
      .pipe(takeUntil(this.ngUnsubscribe)
        , filter((e) => e instanceof NavigationEnd || e instanceof NavigationStart)
      )
      .subscribe((event: NavigationEnd | NavigationStart) => {
        if (event instanceof NavigationEnd) {
          const url = event.urlAfterRedirects;

          if (url.startsWith(this.loginRoute) || url.startsWith(this.setPasswordRoute)) {
            this.isLoginComponent = true;
            this.showBurgerMenu = false;
          } else {
            this.isLoginComponent = false;
            this.showBurgerMenu = true;
            if (this.myNav && this.sidenavMode === 'side') {
              this.myNav.open();
            }
          }
          if (url.startsWith(this.deliveriesListRoute) || url.startsWith(this.statusListRoute)) {
            this.showFilter = true;
          } else {
            this.showFilter = false;
          }
          if (
            url.startsWith(this.deliveriesListRoute) ||
            url.startsWith(this.statusListRoute) ||
            url.startsWith(this.dashboardRoute) && !this.showMobile
          ) {
            this.showRefresh = true;
          } else {
            this.showRefresh = false;
          }
          if (
            url.startsWith(this.deliveryDetailRoute) ||
            url.startsWith(this.transactionDetailRoute) ||
            url.startsWith(this.reportDetailRoute)
          ) {
            this.showBackBtn = true;
          } else {
            this.showBackBtn = false;
          }

          this.dbService.getItem('isVIP').then((value) => {
            this.isVIP = value;
          }).catch(() => {
            this.isVIP = false;
          });

        } else {
          let url = event.url;
          if (url.startsWith(this.deliveryDetailRoute)) {
            url = this.deliveriesListRoute;
          }

          if (this.menuConfig) {

            const currentRouteIndex = this.menuConfig.navigation.findIndex((item) => url.startsWith('/' + item.route));
            if (currentRouteIndex > -1) {
              this.activeMenuItem = currentRouteIndex;
            }
          }

          // prevent loading getting 'stuck'
          this.dataexchangeService.showLoadingIndicator(false);
        }
      });

    this.loginService.afterLogin$.subscribe(() => {
      this.dataManagementVasService.getUserID().then((username) => {
        this.userName = username;
      }).catch((error) => {
        this.userName = null;
        this.logger.error('No user name: ', error);
      });

      this.dataManagementVasService.getUserClass().then((userClass) => {
        this.userClass = userClass;
      }).catch((error) => {
        this.userClass = null;
        this.logger.error('No user class: ', error);
      });

      this.dataManagementVasService.getUserType().then((userType) => {
        this.userType = userType;
      }).catch((error) => {
        this.userType = null;
        this.logger.error('No user type: ', error);
      });

    });

    this.dataexchangeService.titelBarNameChange$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      (title) => {
        this.titleBar = title;
        // change detection works from top to bottom but this is initiated from the child component -> manually detect changes
        this.cdr.detectChanges();
      });

    this.dataexchangeService.viewModeChange$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((mode) => {
      this.viewMode = mode;
    });

    this.dataexchangeService.loadingIndicator$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((visible) => {
      this.showLoading = visible;
      // change detection works from top to bottom but this is initiated from the child component -> manually detect changes
      this.cdr.detectChanges();
    });

    this.httpService.getLocalResource('assets/config/menuConfig.json').subscribe((config: MenuConfig) => {
      this.menuConfig = config;
    });

    this.initSecret();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public openInfoBox() {
    this.deviceService.getDeviceId().then((deviceId) => {
      this.dialogService.openConfirmDialog(
        this.translateVAS.translate('MENU_HELP'),
        'Version: ' + environment.version + ' <br> Device ID: ' + deviceId,
        this.translateVAS.translate('BUTTON_CLOSE'));
    });

  }

  public changeContentSideNav(routingPanel: string, index: number) {
    this.logger.trace('Change to panel : ' + routingPanel);
    // this.activeMenuItem = index;
    this.router.navigate([routingPanel]);
    switch (routingPanel) {
      case 'login': {
        // this is actually the logout but it LEADS to login
        this.loginService.logout();
        break;
      }
      default: {
        break;
      }
    }
    if (this.sidenavMode === 'over') {
      this.myNav.close();
    }
  }

  public toggleFilter() {
    this.dataexchangeService.toggleFilter();
  }

  public refresh() {
    this.dataexchangeService.triggerRefresh();
  }

  public back() {
    this.dataexchangeService.triggerBack();
  }

  private initSecret() {
    const code = from(['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
      'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA']);
    const keys = fromEvent(document, 'keyup').pipe(map((e: KeyboardEvent) => {
      return e.code;
    }));
    const matches = keys.pipe(bufferCount(10, 1), mergeMap((last10) => from(last10).pipe(sequenceEqual(code))));
    matches.pipe(takeUntil(this.ngUnsubscribe), first((x) => x === true)).subscribe((isMatch) => {
      if (isMatch) {
        this.dataexchangeService.activateSecret();
      }
    });
  }
}
